import { useRef, useState } from "react";
import { ImageIcon, Upload, X } from "lucide-react";
import Loader from "../../pages/loader";

const MAX_IMAGES = 5;
const MAX_SIZE_MB = 5;

interface Props {
	open: boolean;
	isLoading: boolean;
	onSkip: () => void;
	onConfirm: (files: File[]) => void;
}

export function ModalImagenesCompletado({
	open,
	isLoading,
	onSkip,
	onConfirm,
}: Props) {
	const [files, setFiles] = useState<File[]>([]);
	const [previews, setPreviews] = useState<string[]>([]);
	const [error, setError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	if (!open) return null;

	const addFiles = (newFiles: FileList | null) => {
		if (!newFiles) return;
		setError(null);
		const valid: File[] = [];
		for (const file of Array.from(newFiles)) {
			if (!file.type.startsWith("image/")) {
				setError("Solo se permiten archivos de imagen.");
				continue;
			}
			if (file.size > MAX_SIZE_MB * 1024 * 1024) {
				setError(`Cada imagen debe pesar menos de ${MAX_SIZE_MB}MB.`);
				continue;
			}
			valid.push(file);
		}
		const combined = [...files, ...valid].slice(0, MAX_IMAGES);
		setFiles(combined);
		setPreviews(combined.map((f) => URL.createObjectURL(f)));
	};

	const removeFile = (index: number) => {
		const next = files.filter((_, i) => i !== index);
		setFiles(next);
		setPreviews(next.map((f) => URL.createObjectURL(f)));
	};

	const handleSkip = () => {
		setFiles([]);
		setPreviews([]);
		setError(null);
		onSkip();
	};

	const handleConfirm = () => {
		onConfirm(files);
	};

	return (
		<div className='fixed inset-0 z-60 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm'>
			<div
				role='dialog'
				aria-modal='true'
				className='relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl'
			>
				<div className='mb-5 flex items-start gap-4'>
					<div className='flex h-12 w-12 items-center justify-center rounded-full bg-blue-100'>
						<ImageIcon className='h-6 w-6 text-blue-600' />
					</div>
					<div className='flex-1'>
						<h3 className='text-lg font-semibold text-slate-900'>
							Imágenes del servicio completado
						</h3>
						<p className='mt-1 text-sm text-slate-500'>
							Puedes cargar hasta {MAX_IMAGES} imágenes del trabajo realizado
							para tu portafolio. Este paso es opcional.
						</p>
					</div>
				</div>

				<input
					ref={inputRef}
					type='file'
					accept='image/*'
					multiple
					className='hidden'
					onChange={(e) => {
						addFiles(e.target.files);
						// Reset input so the same file can be re-selected after removal
						e.target.value = "";
					}}
				/>

				{files.length < MAX_IMAGES && (
					<button
						type='button'
						onClick={() => inputRef.current?.click()}
						className='mb-4 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 py-5 text-sm font-medium text-slate-500 transition hover:border-blue-400 hover:text-blue-600'
					>
						<Upload className='h-5 w-5' />
						Seleccionar imágenes ({files.length}/{MAX_IMAGES})
					</button>
				)}

				{previews.length > 0 && (
					<div className='mb-4 grid grid-cols-3 gap-2 sm:grid-cols-5'>
						{previews.map((src, i) => (
							<div
								key={src}
								className='relative aspect-square overflow-hidden rounded-lg border border-slate-200'
							>
								<img
									src={src}
									alt={`Imagen ${i + 1}`}
									className='h-full w-full object-cover'
								/>
								<button
									type='button'
									onClick={() => removeFile(i)}
									className='absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-white shadow'
									aria-label='Eliminar imagen'
								>
									<X className='h-3 w-3' />
								</button>
							</div>
						))}
					</div>
				)}

				{error ? (
					<p className='mb-3 text-xs text-rose-600'>{error}</p>
				) : null}

				{isLoading ? (
					<div className='mb-4 flex items-center justify-center gap-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm font-medium text-slate-600'>
						<span className='scale-50 origin-center'>
							<Loader />
						</span>
						<span>Subiendo imágenes y completando el pedido...</span>
					</div>
				) : null}

				<div className='flex justify-end gap-3'>
					<button
						type='button'
						onClick={handleSkip}
						disabled={isLoading}
						className='rounded-full px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 disabled:opacity-60'
					>
						Saltar
					</button>
					<button
						type='button'
						onClick={handleConfirm}
						disabled={isLoading || files.length === 0}
						className='rounded-full bg-[#008db1] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#007491] disabled:cursor-not-allowed disabled:opacity-60'
					>
						{isLoading ? "Subiendo..." : "Subir y completar"}
					</button>
				</div>
			</div>
		</div>
	);
}
